import { I18n } from '@calendly/i18n'
import {
  ArrowRoundRightIcon,
  CheckIcon,
  EventTypeIcon,
  MailSendEnvelopeIcon,
  PhoneIcon,
  TimeClockCircleIcon
} from '@calendly/ui/components/icons'
import { BodyText } from '@calendly/ui/components/typography'
import { colorTextStandard } from '@calendly/ui/theme'
import { useSelector } from 'react-redux'

import { dateNotationSelector, timeNotationSelector, timezoneSelector } from 'selectors/current_user'

import { StepTime } from './step_time'
import { Description, IconContainer, TimelineStep, Container, RescheduleReasonText } from './styles'

const Step = ({ Icon, timeString, description, children }) => {
  const timezone = useSelector(timezoneSelector)
  const dateNotation = useSelector(dateNotationSelector)
  const timeNotation = useSelector(timeNotationSelector)

  return (
    <TimelineStep>
      <IconContainer>
        <Icon />
      </IconContainer>
      <Container>
        <Description>
          <BodyText weight="bold" color={colorTextStandard}>
            {description}
          </BodyText>
          <StepTime
            timeString={timeString}
            timezone={timezone}
            dateNotation={dateNotation}
            timeNotation={timeNotation}
          />
        </Description>
        {children}
      </Container>
    </TimelineStep>
  )
}

export const BookedStep = ({ selectedEvent }) => {
  const description = selectedEvent.eventType.kind === 'group'
    ? I18n.t('publisher.meetings.timeline.steps.booked_group')
    : I18n.t('publisher.meetings.timeline.steps.booked_solo', { name: selectedEvent.invitee.name })
  return (
    <Step
      Icon={EventTypeIcon}
      timeString={selectedEvent.scheduledAt}
      description={description}
    />
  )
}

export const OriginalEventBookedStep = ({ selectedEvent }) => {
  const description = selectedEvent.eventType.kind === 'group'
    ? I18n.t('publisher.meetings.timeline.steps.booked_group')
    : I18n.t('publisher.meetings.timeline.steps.booked_solo', { name: selectedEvent.invitee.name })
  return (
    <Step
      Icon={EventTypeIcon}
      timeString={selectedEvent.oldSchedulable.createdAt}
      description={description}
    />
  )
}

export const StartsStep = ({ selectedEvent }) => {
  const description = selectedEvent.started
    ? I18n.t('publisher.meetings.timeline.steps.event_started')
    : I18n.t('publisher.meetings.timeline.steps.event_starts')
  return (
    <Step
      Icon={TimeClockCircleIcon}
      timeString={selectedEvent.startTime}
      description={description}
    />
  )
}

export const RescheduledStep = ({ selectedEvent }) => (
  <Step
    Icon={ArrowRoundRightIcon}
    timeString={selectedEvent.cancellation.cancelledAt}
    description={I18n.t('publisher.meetings.timeline.steps.rescheduled', { name: selectedEvent.cancellation.cancelledBy })}
  >
    <RescheduleReasonText>
      {selectedEvent.cancellation.reason.replace(I18n.t('publisher.dashboard.event.rescheduled_prefix'), '')}
    </RescheduleReasonText>
  </Step>
)

export const CancelledStep = ({ selectedEvent }) => (
  <Step
    Icon={ArrowRoundRightIcon}
    timeString={selectedEvent.cancellation.cancelledAt}
    description={I18n.t('publisher.meetings.timeline.steps.canceled', { name: selectedEvent.cancellation.cancelledBy })}
  >
    <RescheduleReasonText>
      {selectedEvent.cancellation.reason.replace(I18n.t('publisher.dashboard.event.rescheduled_prefix'), '')}
    </RescheduleReasonText>
  </Step>
)

export const FromRescheduledStep = ({ selectedEvent }) => (
  <Step
    Icon={ArrowRoundRightIcon}
    timeString={selectedEvent.oldSchedulable.cancelledAt}
    description={I18n.t('publisher.meetings.timeline.steps.rescheduled', { name: selectedEvent.oldSchedulable.cancelledBy })}
  >
    <RescheduleReasonText>
      {selectedEvent.oldSchedulable.reason}
    </RescheduleReasonText>
  </Step>
)

export const ReconfirmationStep = ({ entry }) => (
  <Step
    Icon={CheckIcon}
    timeString={entry.timestamp}
    description={I18n.t('publisher.meetings.timeline.steps.reconfirmed', { name: entry.inviteeName })}
  />
)

export const WorkflowStep = ({ workflow }) => {
  const Icon = workflow.actionConfigurationType.startsWith('booking.sms') ? PhoneIcon : MailSendEnvelopeIcon

  return (
    <Step
      Icon={Icon}
      timeString={workflow.timestamp}
      description={workflow.workflowName}
    />
  )
}
